export interface TenantAccountBillingStatusHistory {
  id: string;
  status: string;
  reasons: string[];
  effectiveDate: string;
  priorStatus: string;
  priorReasons: string[];
  modifiedBy: string;
  modifiedAt: string;
  modifiedDescription?: string;
  createdBy: string;
}

export interface TenantAccountCurrentBillingStatus {
  status: string;
  reasons: string[];
  effectiveDate: string;
}

export interface TenantAccountBillingStatusResponse {
  current: TenantAccountCurrentBillingStatus | null;
  history: TenantAccountBillingStatusHistory[];
}

export interface TenantAccountStatusResponse {
  status: string;
  cancellationReason?: string | null;
  cancellationNoticeDays?: number | null;
  pendingCloseStartedAt?: string | null;
}

export type CcFeeMode = 'NONE' | 'FLAT_PER_TXN' | 'PERCENT' | 'PASS_THROUGH';
export type CcFeePolicySource = 'TESA_SCHEDULE' | 'ACCOUNT_OVERRIDE';

export interface CcFeePolicyScheduleView {
  tesaVersion: string;
  mode: CcFeeMode;
  flatAmount: string | null;
  monthlyCapAmount: string | null;
  startDate: string;
  endDate: string;
}

export interface CcFeePolicyPassThroughView {
  creditPercent: string;
  creditFlatAmount: string | null;
  debitPercent: string;
  debitFlatAmount: string;
  startDate: string;
  endDate: string;
}

/** In-force card-fee policy from GET /tenant-account/:id/cc-fee-policy. Percents are percentage points. */
export interface CcFeePolicyAdmin {
  charging: boolean;
  policyCuid: string | null;
  source: CcFeePolicySource | null;
  tesaVersion: string | null;
  mode: CcFeeMode | null;
  percent: string | null;
  flatAmount: string | null;
  monthlyCapAmount: string | null;
  startDate: string | null;
  endDate: string | null;
  reason: string | null;
  schedule: CcFeePolicyScheduleView | null;
  passThrough: CcFeePolicyPassThroughView | null;
}

export interface Tenant {
  id: string
  name: string
  salesforceAccountId?: string
  /** Display name from sfdc.account when available. */
  salesforceAccountName?: string | null
  contacts: Contact[]
  property: Property
  serviceAccounts: ServiceAccount[]
  notes: Note[]
  isNational?: boolean | null
  authorizeSpecificAccountsOnly?: boolean | null
  ein?: string | null
}

export interface TenantItem {
  id: string;
  name: string;
  contacts: Contact[];
  property: Property;
  /** Nested accounts for hierarchical list (standing chips). */
  serviceAccounts?: TenantAccountListItem[];
}

/** Light account standing for the tenants list hierarchy (GET /tenant). */
export interface TenantAccountListItem {
  id: string;
  kingAccountName: string;
  kingAccountNumber: string;
  dueBalanceTenantToCorp?: number;
  billedBalanceTenantToCorp?: number;
  accountStatus?: string;
  cancellationReason?: string | null;
  cancellationNoticeDays?: number | null;
  pendingCloseStartedAt?: string | null;
  isFinalAccount?: boolean;
  utilityStanding?: 'ACTIVE' | 'INACTIVE' | 'UNKNOWN';
  arStanding?: 'CURRENT' | 'PAST_DUE' | 'PAYMENT_PLAN_CURRENT' | 'PAYMENT_PLAN_PAST_DUE';
  daysPastDue?: number | null;
  vnemStatus?: 'ON_TABLE' | 'PENDING_OFF' | 'OFF_TABLE' | 'PENDING_ON' | 'ON_HOLD' | 'UNKNOWN';
  billingConfiguration: {
    tenantAccountConfiguration: {
      billingMode?: string;
    };
  };
  mailingPreference: {
    paperless: boolean;
  };
  paymentPreference: {
    autopay: {
      enabled: boolean;
    };
  };
  serviceAgreements: Array<{
    active: boolean;
    optiRateDetails?: { status: string };
  }>;
}

export interface ServiceAccount {
  id: string;
  kingAccountName: string;
  kingAccountNumber: string;
  utilityAccountCuid?: string;
  utilityAccountName: string;
  utilityAccountNumber: string;
  utilityName: string;
  billingAddress: Address;
  billingConfiguration: BillingConfig;
  incentivePlans: IncentivePlans;
  mailingPreference: MailingPreference;
  paymentPreference: PaymentPreference;
  serviceAgreements: ServiceAgreement[];
  accountManagers: AccountManager[];
  billingEvents: TenantAccountBillingEvent[];
  /** Active programs linked via this account's meters (ProgramMeter). */
  programs?: { id: string; name: string }[];

  payments: Payment[];
  /** Latest non-SKIPPED invoice from GET /tenant; null when the account has none yet. */
  currentInvoice: Invoice | null;
  dueBalanceTenantToCorp?: number; // Amount currently due (for messaging to tenant)
  billedBalanceTenantToCorp?: number; // Total outstanding balance (for payment validation max)

  /** TenantAccount.status — KE Account chip source. */
  accountStatus?: string;
  cancellationReason?: string | null;
  cancellationNoticeDays?: number | null;
  pendingCloseStartedAt?: string | null;
  /** Derived Final billing-mode display (CLOSED + settled); not a stored BillingMode. */
  isFinalAccount?: boolean;
  /** Rollup of meter services for Utility Account chip. */
  utilityStanding?: 'ACTIVE' | 'INACTIVE' | 'UNKNOWN';
  /** Finance AR Billing Standing chip. */
  arStanding?: 'CURRENT' | 'PAST_DUE' | 'PAYMENT_PLAN_CURRENT' | 'PAYMENT_PLAN_PAST_DUE';
  daysPastDue?: number | null;
  /** VNEM table lifecycle chip. */
  vnemStatus?: 'ON_TABLE' | 'PENDING_OFF' | 'OFF_TABLE' | 'PENDING_ON' | 'ON_HOLD' | 'UNKNOWN';
  billingOpsStatus?: {
    status: string;
    reasons: string[];
    effectiveDate: string | null;
  } | null;
}

export interface Payment {
  id: string;
  paymentNumber: string;
  paymentDate: string;
  postingDate: string;
  amount: number;
  status: string;
  paymentMethodId: string;
  type: string;
  providerName: string;
  last4: string;
  memo: string;
  canCancel: boolean | null;
  canChargeback: boolean | null;
  canFail: boolean | null;
  canProcess: boolean | null;
  canSucceed: boolean | null;
  tenant: {
    id: string;
    name: string;
  };
  property: {
    name: string;
  };
  serviceAccount: { number: string };
  invoice: { number: string };
  billingMode: string | null;
  paymentMethod: PaymentMethod;
  paymentTypeDisplay: string;
  display: string;
  nextStatus?: string[];
  failureReason?: string;
}

export interface Invoice {
  id: string
  amount: number
  remainingBalance: number
  status: InvoiceStatus
  number: number
  dueDate: string
  billingStartDate: string
  billingEndDate: string
  dueBalanceTenantToCorp?: number // Amount currently due (for messaging to tenant)
  billedBalanceTenantToCorp?: number // Total outstanding balance (for payment validation max)
}

export type InvoiceStatus =
  | "PENDING"
  | "PROCESSING"
  | "PAID"
  | "PAST_DUE"
  | "VOID";

export interface CollectionsInvoice {
  id: string
  tenant: {
    id: string
    name: string
    propertyName: 'King Property - Somewhere, CA'
    contact: Contact | null
    utilityName?: string
  }
  tenantAccountId: string
  tenantAccountNumber: string
  number: number
  billingStart: string
  billingEnd: string
  due: string
  lateBy: string | null
  statementDate: string
  originalBalance: number
  remainingBalance: number
  amountDue: number
  pastDueBalance: number
  unpaidInvoiceBalance: number
  paymentStatus: string
  daysPastDue: number
  periodChargeAccrual: number
  paymentMethod: PaymentMethod | null
  status: InvoiceStatus
  memo: string | null
  nextStatus: string[]
  externalStatementAvailable: boolean
  hasOptiRateAudit?: boolean
  canEditDates: boolean
  isLatestInvoice: boolean
}

export type StatementType = 'king' | 'utility' | 'optirate_audit'

export interface TenantAccountBillingEvent {
  id: string
  eventId: string
  eventType: string
  eventSubType: string
  transactionDate: string
  postingDate: string
  amount: number
  entries: TenantAccountBillingEventEntry[]
}

export interface TenantAccountBillingEventEntry {
  id: string;
  accountName: string;
  propertyName: string;
  invoiceNumber: string | null;
  eventId: string;
  entryId: string;
  eventType: string;
  eventSubtype: string;
  entryType: string;
  postDate: string;
  txDate: string;
  amount: number;
  grossKECharge: number;
  keDiscount: number;
  utilityChargeTenantToCorp: number;
  utilityChargeCorpToUtility: number;
  unbilledPayableTenantToCorp: number;
  billedPayableTenantToCorp: number;
  duePayableTenantToCorp: number;
  billedPayableCorpToUtility: number;
  duePayableCorpToUtility: number;
  corpNsfCharge: number;
  corpLateFeeCharge: number;
  corpProcessingCharge: number;
  miscCharge: number;
  balanceOnDate: string;
  unbilledBalanceTenantToCorp: number;
  billedBalanceTenantToCorp: number;
  dueBalanceTenantToCorp: number;
  billedBalanceCorpToUtility: number;
  dueBalanceCorpToUtility: number;
  hasKEAccrual: boolean;
  lifetimeSavings: number;
  sourceEntity?: string;
}

export interface BillingEventAuditLogEntry {
  id: string;
  cuid: string;
  createdAt: string;
  createdBy: string;
  modifiedAt: string;
  modifiedBy: string;
  modifiedDescription: string | null;
  destination: string;
  status: string;
  eventBody: {
    eventType?: string;
    eventSubType?: string;
    amount?: string;
    postingDate?: string;
    transactionDate?: string;
    transactionStartDate?: string;
    tenantAccountCuid?: string;
    sourceEntity?: string;
    actorId?: string;
    billingData?: any;
    skipChecks?: boolean;
  };
}

export interface UpdateAuditLogStatusRequest {
  status: 'QUEUED' | 'SKIPPED';
  modifiedDescription: string;
}

export interface UpdateAuditLogEventBodyRequest {
  eventType: string;
  transactionStartDate?: string;
  transactionDate: string;
  sourceEntity?: string;
  amount?: number;
  skipChecks?: boolean;
  eventSubType?: string;
  modifiedDescription: string;
}

export interface Contact {
  firstName: string;
  lastName: string;
  phone: string;
  emailAddress: string;
}

export interface Property {
  id: string;
  name: string;
  location: string;
  utilityShortName: string;
  utilityLoginUrl?: string;
  programs?: { id: string; name: string }[];
}

export interface Utility {
  id: string;
  name: string;
  shortName: string;
  type?: string | null;
  loginUrl?: string | null;
}

export interface ServiceAgreement {
  id: string;
  startDate: Date;
  endDate: Date;
  active?: boolean;
  serviceStatus?: string | null;
  serviceAgreementId: string;
  serviceAddress: Address;
  meter: Meter;
  tariff: Tariff;
  ccaTariff?: Tariff | null;
  tariffEffectiveDate?: string | Date | null;
  tariffEndDate?: string | Date | null;
  ccaTariffEffectiveDate?: string | Date | null;
  ccaTariffEndDate?: string | Date | null;
  ccaVintageYear?: string | null;
  meterAccountType?: 'BENEFITTING' | 'GENERATOR' | null;
  postBillingTransition?: boolean;
  /** True when the backend comparison rules allow at least one OptiRate target tariff. */
  optiRateEligible?: boolean;
  optiRateDetails?: OptiRateDetails;
  lastBilledDate: Date | null;
  /** VNEM table lifecycle for this meter/SA. */
  vnemStatus?: 'ON_TABLE' | 'PENDING_OFF' | 'OFF_TABLE' | 'PENDING_ON' | 'ON_HOLD' | 'UNKNOWN';
  /**
   * When still ON_TABLE but a newer unactioned submission changes % and/or tariff
   * for this SA (reallocation in flight).
   */
  vnemPendingChange?: {
    programId: string
    percentage?: { from: number; to: number }
    tariffSchedule?: { from: string | null; to: string | null }
  } | null
  /** Programs linked via ProgramMeter for this meter. */
  programs?: { id: string; name: string }[];
}

export interface Meter {
  cuid: string
  meterNumber: string
  greenButtonEnrolled?: boolean
  salesforceMeterId?: string
  /** Display name from sfdc.meter_data when available. */
  salesforceMeterName?: string | null
}

export type CreditPayee = 'PROJECT_CO_REIMBURSE' | 'TOP_CO_REIMBURSE'

export const CREDIT_PAYEE_LABELS: Record<CreditPayee, string> = {
  PROJECT_CO_REIMBURSE: 'ProjectCo Reimburse',
  TOP_CO_REIMBURSE: 'TopCo Reimburse',
}

export interface OptiRateDetails {
  requestDate: string;
  requestedBy: string;
  effectiveCycleStartDate?: string;
  originalTariff: string;
  targetTariff: string;
  status: 'NOT_ENROLLED' | 'ENROLLED' | 'PENDING' | 'ARCHIVED';
  creditPayee?: CreditPayee;
  originalSAId?: string;
  optiRateSAId?: string;
}

export interface Tariff {
  name: string;
  schedule: string;
  utilityShortName?: string | null;
}

export interface MailingPreference {
  mailingAddress: Address;
  recipientName: string;
  paperless: boolean;
}

export interface PaymentPreference {
  autopay: Autopay;
  paymentMethodId: string;
}

export interface AccountManager {
  id: string
  email: string
  lastLoginDate?: Date
  isActive: boolean
  accountSignupLink?: string
  profile?: {
    firstName?: string
    lastName?: string
    phoneNumber?: string
  }

  paymentMethods: PaymentMethod[]
}

export interface PaymentMethod {
  id: string;
  type: string;
  last4: string;
  providerName: string;
  paymentMethodType: string;
  default: boolean | undefined;
}

export interface Autopay {
  enabled: boolean;
}

export interface Note {
  cuid: string
  body: string
  sourceEntity: string
  createdAt: Date
  modifiedAt: Date
  isDeleted: boolean
  isPinned: boolean
  tags: string[]
  author: {
    cuid: string
    email: string | null
    firstName: string | null
    lastName: string | null
  }
  sourceEntityName?: string
  createdBy: string
  salesforceTaskId?: string | null
}

export interface NoteSearchParams {
  body?: string
  sourceEntity?: string
  startDate?: Date
  endDate?: Date
  tags?: string[]
  isPinned?: boolean
  sortIsPinned?: 'asc' | 'desc'
  sortCreatedAt?: 'asc' | 'desc'
  page?: number
  resultsPerPage?: number
}

export interface Address {
  zip: string;
  city: string;
  state: string;
  street1: string;
  street2: string;
  type: string;
}

export interface InvoiceSummary {
  number: number;
  amount: number;
}

export interface InvoiceSummaries {
  PENDING: InvoiceSummary;
  PROCESSING: InvoiceSummary;
  PAID: InvoiceSummary;
  PAST_DUE: InvoiceSummary;
  VOID: InvoiceSummary;
  PAID_EXTERNALLY: InvoiceSummary;
  BALANCE_CARRY_FORWARD: InvoiceSummary;
  SKIPPED: InvoiceSummary;
}

export interface PaymentSummary {
  number: number;
  amount: number;
}

export interface PaymentSummaries {
  CANCELLED: InvoiceSummary;
  CHARGEBACK: InvoiceSummary;
  FAILED: InvoiceSummary;
  PENDING: InvoiceSummary;
  PROCESSING: InvoiceSummary;
  SUCCEEDED: InvoiceSummary;
}

export interface Paginated<T> {
  total?: number;
  resultsPerPage?: number;
  page?: number;
  results: T[];
}

export interface BalanceSummary {
  balanceTotal: string;
  balanceDue: string;
}

export interface ExtendedPaginated<T> extends Paginated<T> {
  summary?: BalanceSummary;
}

export interface PaginationArgs {
  resultsPerPage?: number;
  page?: number;
}

export interface GetTenantSearchArgs {
  searchTerm: string;
}

export interface GetTenantSearchData {
  type: 'PROPERTY' | 'TENANT' | 'PROGRAM'
  utilityShortName: string
  propertyCuid: string
  propertyName: string
  propertyLocation: string
  tenantCuid?: string
  programCuid?: string
  programName?: string
  contactFirstName?: string
  contactLastName?: string
  contactFullName?: string
  contactPhone?: string
  contactEmail?: string
  accountManagerIdentifier?: string
  accountManagerFirstName?: string
  accountManagerLastName?: string
  accountManagerFullName?: string
  accountManagerPhone?: string
  tenantAccountCuid?: string
  tenantAccountName?: string
  tenantAccountNumber?: string
  utilityAccountCuid?: string
  utilityAccountName?: string
  utilityAccountNumber?: string
  meterCuid?: string
  meterNumber?: string
  meterServiceCuid?: string
  serviceAccountId?: string
  serviceStreetAddress?: string
  billingStreetAddress?: string
  mailingStreetAddress?: string
  matchedOn: string[] | []
}

export interface GetTenantSearch {
  success: boolean;
  message?: string;
  searchTerm: string;
  data: GetTenantSearchData[] | [];
}

export interface GetTenantsArgs {
  tenantName?: string;
  propertyId?: string;
  utilityId?: string;
  page?: number;
  resultsPerPage?: number;
}
export type GetTenantsData = Paginated<TenantItem>;

export type GetTenantArgs = string;
export type GetTenantData = Tenant;

export interface GetInvoicesListArgs {
  tenantId?: string;
  tenantAccountId?: string;
  propertyId?: string;
  status?: InvoiceStatus;
  invoiceNumber?: string;
  page?: number;
  resultsPerPage?: number;
}
export type GetInvoicesListData = ExtendedPaginated<CollectionsInvoice>;

export interface GetPaymentsArgs {
  tenantId?: string
  propertyId?: string
  tenantAccountId?: string
  status?: null | string | 'PENDING' | 'PROCESSING' | 'SUCCEEDED' | 'FAILED' | 'CANCELLED' | 'CHARGEBACK'
  page?: number
  resultsPerPage?: number
}
export type GetPaymentsData = Paginated<Payment>;

export interface UpdatePaymentRequest {
  paymentId: string;
  newStatus?: string;
  memo?: string;
  transactionDate?: string; // Optional custom transaction date for billing events (e.g., chargeback date)
}

export interface File {
  id: string;
  cuid: string;
  driveCuid: string;
  parentFileId: string;
  fileId: string;
  fileName: string;
  fileExtension: string;
  filePath: string;
  fileMimeType: string;
  fileSize: number;
  lastModifiedName: string;
  lastModifiedPermissionId: string;
  lastModifiedDate: Date;
  deleted: boolean;
  opportunity?: string;
  tag?: string;
}

export interface FilePath {
  fileId: string;
  fileName: string;
}

export interface Opportunity {
  [id: string]: string;
}
export interface LastModifiedObject {
  [lastModifiedPermissionId: string]: string;
}

export interface DirectoryObject {
  [directoryName: string]: string;
}

export interface Tag {
  index: number;
  item: string;
  owner: string;
  keyword: string;
}

export type FilesResult = Paginated<File>;

export interface GetFilesArgs {
  page?: number;
  resultsPerPage?: number;
  fileName?: string;
  fileExtension?: string | null;
  fileMimeType?: string | null;
  lastModifiedPermissionId?: string | null;
  startDate?: number | null;
  endDate?: number | null;
  opportunity?: string | null;
  tag?: string | null;
  sortColumn?: string | null;
  sortDirection?: string | null;
  parentFileId?: string | null;
  role?: string | null;
}

export interface GetProgramsArgs extends PaginationArgs {
  page?: number;
  resultsPerPage?: number;
  programName?: string;
  propertyId?: string;
}

export type GetProgramsListData = Paginated<ProgramItem>;

export interface ProgramItem {
  id: string;
  name: string;
  utility: string;
  startDate: string;
  endDate?: string;
  ptoDate: string;
  billingOnDate: string;
  billingOn: boolean;
  propertyName?: string;
  totalVNEMAllocationPercentage?: number;
}

export interface DetailedProgramItem {
  id: string;
  name?: string;
  utility: string;
  startDate: string;
  endDate?: string;
  ptoDate: string;
  billingOnDate: string;
  billingOn: boolean;
  salesforceId?: string;
  salesforceName?: string | null;
  createdAt: string;
  modifiedAt: string;
  modifiedDescription?: string;
  type: string;
  billingDelayCycles: number;
}

export interface Vnem {
  submissionDate: Date;
  effectiveDate: Date;
  vnemVersion: string;
  totalAllocation: number;
}

export interface VnemAllocationItem {
  utilityAccounts: [
    {
      utilityAccountNumber: string;
      tenantAccounts: [
        {
          accountName: string;
          accountNumber: string;
        }
      ];
      serviceAgreements: [
        {
          serviceAgreementNumber: string;
          meterNumber: string;
          tariff: string;
          serviceAddress: string;
        }
      ];
    }
  ];
  oneBillAccountCreated: boolean;
  utilityAccountLinked: boolean;
  ngomPaired: boolean;
  greenButtonPaired: boolean;
  creditMeter: boolean;
  allocation: number;
}
export interface AllocationReadinessResult {
  allocationSum: number;
  doesAllocationSumTo100: boolean;
  doesActiveAllocationAlreadyExist: boolean;
  activeAllocationSubmissionDate?: string;
  activeAllocationStartDate?: string;
  activeAllocationEndDate?: string;
  allocationSubmission: AllocationSubmission;
  allocations: AllocationReadinessItem[];
}

export interface AllocationSubmission {
  submissionDate: Date;
  allocations: Allocation[] | null;
}

export interface AllocationReadinessItem {
  allocation: Allocation;
  oneBill: allocationOneBillComparison;
  readiness: AllocationReadiness;
  smdEnrollment?: SmdEnrollmentInfo;
}

export interface SmdEnrollmentInfo {
  isEnrolled: boolean;
  usagePointId: string | null;
  minIntervalDate: string | null;
  maxIntervalDate: string | null;
}

export interface AllocationReadinessTableRecord {
  id: string;
  source: string;
  allocation?: Allocation;
  oneBill?: allocationOneBillComparison;
}

export interface AllocationReadiness {
  doesTenantAccountNameMatch: boolean;
  doesUtilityAccountNameMatch: boolean;
  doesServiceAddressMatch: boolean;
  doesUtilityAccountNumberMatch: boolean;
  doesServiceAgreementMatch: boolean;
  doesMeterNumberMatch: boolean;
  doesTariffMatch: boolean;
  allocationSum: number;
  doesAllocationSumTo100: boolean;
  doesActiveAllocationAlreadyExist: boolean;
  isPmaApproved: boolean;
  isOnActiveTable: boolean;
  vaTableStatus: string | null;
}

export interface Allocation {
  salesforceAllocationId: string | null;
  submissionDate: Date | null;
  accountName: string | null;
  utilityAccountName: string | null;
  utilityAccountNumber: string | null;
  serviceAgreementId: string | null;
  meterNumber: string | null;
  serviceAddress: string | null;
  tariff: string | null;
  apn: string | null;
  allocation: string | null;
  salesforceProgramId: string | null;
  salesforceMeterDataId: string | null;
  salesforceAccountId: string | null;
  designatedUnallocatedCreditAccount?: boolean;
  pmaApproved: boolean | null;
  onActiveTable: boolean | null;
  vaTableStatus: string | null;
}

export interface allocationOneBillComparison {
  tenantAccountName: string;
  utilityAccountName: string;
  serviceAddress: {
    address1?: string;
    address2?: string | null;
    city?: string;
    state?: string;
    postalCode?: string;
    type?: string;
  };
  utilityAccountNumber: string;
  serviceAgreement: string;
  meterNumber: string;
  tariff: string;
}

export interface WholeProgramWarnings {
  allocationSum: number;
  doesAllocationSumTo100: boolean;
  doesActiveAllocationAlreadyExist: boolean;
  activeAllocationSubmissionDate?: string;
  activeAllocationStartDate?: string;
  activeAllocationEndDate?: string;
}

/**
 * Represents a billing cycle with interval data status
 */
export interface BillingCycleInfo {
  startDate: Date | null;
  endDate: Date | null;
  billDays: number | null;
  intervalDaysWithData: number | null;
  isIntervalDataMissing: boolean;
}

/**
 * SDGE-specific interval data status for UI display.
 * Supports three scenarios:
 * 1. VNEM_NOT_APPLIED - VNEM credits not applied to latest billing cycle (no action)
 * 2. INTERVAL_DATA_EXISTS - Interval data exists for latest billing cycle (no action)
 * 3. INTERVAL_DATA_MISSING - Latest billing cycle missing interval data (show button)
 */
export type SdgeIntervalDataStatusType = 'VNEM_NOT_APPLIED' | 'INTERVAL_DATA_EXISTS' | 'INTERVAL_DATA_MISSING';
export interface SdgeIntervalDataStatus {
  status: SdgeIntervalDataStatusType;
  statusMessage: string;
  showPullIntervalDataButton: boolean;
  lastBillingCycle: {
    startDate: Date | null;
    endDate: Date | null;
  };
  nextBillingCycle: {
    startDate: Date | null;
    endDate: Date | null;
  };
  billingCyclesWithMissingData: BillingCycleInfo[];
}

export interface ReadinessChecklist {
  allocationSubmission: {
    isSubmitted: boolean
    submissionDate?: string
    startDate?: string
    endDate?: string
    totalAllocation?: number
    hasUnallocatedCreditMeter?: boolean
  }
  portalLinking: {
    isLinked: boolean
    utilityUsername?: string
    shortName?: string
    scraperPropertyCuid?: string
    scraperLastRun?: string
    isScraperActive?: boolean
    isBillProcessingActive?: boolean
  },
  intervalDataStatus?: IntervalDataReadinessChecklistItem
}

export interface IntervalDataReadinessChecklistItem {
  dataRange: {
    minDate: Date | null;
    maxDate: Date | null;
  };
  shareMyData: {
    isEligible: boolean;
    isEnrolled: boolean;
    lastEnrollmentDate: Date | null;
  };
  lastBillingCycle: {
    startDate: Date | null;
    endDate: Date | null;
    isIntervalDataOutdated: boolean;
  };
  canPullIntervalData: boolean;
  blockedReasons: string[];
  sdgeStatus: SdgeIntervalDataStatus | null;  // Only populated for SDGE utilities
  latestJob: RenderJobResponse
}

interface RenderJobResponse {
  createdAt: string; // "2025-12-17T00:10:00.906856Z",
  finishedAt: string; // "2025-12-17T00:14:09Z",
  id: string; // "job-d50v9m6uk2gs7383pqpg",
  planId: string; // "plan-crn-005",
  serviceId: string; // "crn-ceub2a82i3ml697om9kg",
  startCommand: string; // "CONFIG_FILE_PATH=\"/etc/secrets/.env\" node apps/king-jobs/dist/main --run-job sdge-accounts-scraper --remitPayments",
  startedAt: string; // "2025-12-17T00:10:01Z",
  status: 'created' | 'pending' | 'running' | 'succeeded' | 'failed' | 'canceled'; // "succeeded"
}

export interface UtilityPortalCredentials {
  username: string
  password: string
  shortName?: string
}

export interface LinkUtilityPortalResult {
  success?: boolean
  message?: string
}

export interface UpdateScraperIsActiveResult {
  isScraperActive: boolean
  isBillProcessingActive: boolean
  success?: boolean
  message?: string
}

export interface UpdateProgramBody {
  billingOnDate?: string;
  ptoDate?: string;
  startDate?: string;
  endDate?: string;
  salesforceId?: string;
  billingOn?: boolean;
}

export interface CreateProgramBody {
  type: string;
  startDate: string;
  endDate?: string;
  billingDelayCycles: number;
  billingOn?: boolean;
  propertyCuid: string;
  billingOnDate: string;
  ptoDate: string;
  name?: string;
  salesforceId?: string;
}

export interface CreateProgramResponse {
  success: boolean;
  message: string;
  programId?: string;
}

export interface SmdJobResponse {
  success: boolean
  message: string
  error?: string
  job?: {
    id: string
    status: string
    startCommand: string
    createdAt: string
  }
}

export interface BillingOptionsResponse {
  utilityBillTypes: string[];
  kingBillTypes: string[];
  enrollmentStatuses: string[];
  billingMode: string[];
  billingStatusOptions: string[];
  billingStatusReasons: string[];
  accountStatusOptions: string[];
}

export interface BillingOptionObject {
  [key: string]: string;
}
export interface BillingOptionsResult {
  utilityBillTypes: BillingOptionObject[];
  kingBillTypes: BillingOptionObject[];
  enrollmentStatuses: BillingOptionObject[];
  billingMode: BillingOptionObject[];
  billingStatusOptions: BillingOptionObject[];
  billingStatusReasons: BillingOptionObject[];
  accountStatusOptions: BillingOptionObject[];
}

export interface BillingConfig {
  tenantAccountConfiguration?: {
    billingMode?: string;
    billType?: string;
    energyDiscount?: number;
    demandDiscount?: number;
    includeChart?: boolean;
    includeSummary?: boolean;
  };
  utilityAccountConfiguration?: {
    billType?: string;
    ccaProgram?: string;
    demandCreditProgram?: string;
  };
}

export interface InstallmentPreview {
  id: string;
  number: number;
  amount: number;
  dueDate: string;
}

export interface Installment {
  id: string;
  number: number;
  amount: number;
  remainingBalance: number;
  status: string;
  dueDate: string;
  paidDate?: string;
  paidAmount?: number;
  billedDate?: string;
}

interface ExpandedCreatedBy {
  cuid: string;
  email: string;
  firstName?: string;
  lastName?: string
}
export interface IncentivePlan {
  id: string;
  humanId?: string;
  createdAt: string;
  createdBy: ExpandedCreatedBy;
  startingBalance: string;
  remainingBalance: string;
  status: string;
  type: string;
  cadence: string;
  installmentCount: number;
  startDate: string;
  endDate?: string;
  memo?: string;
  installments: Installment[];
  cancellationReason?: string;
  cancelledBy?: string;
  cancelledAt?: string;
  reversalReason?: string;
  reversedBy?: string;
  reversedAt?: string;
}

export interface IncentivePlans {
  tenantAccountIncentivePlans: IncentivePlan[];
  utilityAccountIncentivePlans: IncentivePlan[];
}

export interface IncentivePlanOptionsResponse {
  incentivePlanTypes: string[];
  incentivePlanStatuses: string[];
  incentivePlanCadences: string[];
  incentivePlanInstallmentStatuses: string[];
}

export interface IncentivePlanOptionObject {
  [key: string]: string;
}

export interface IncentivePlanOptionsResult {
  incentivePlanTypes: IncentivePlanOptionObject[];
  incentivePlanStatuses: IncentivePlanOptionObject[];
  incentivePlanCadences: IncentivePlanOptionObject[];
  incentivePlanInstallmentStatuses: IncentivePlanOptionObject[];
}

export interface UpdateUtilityBillingConfigBody {
  billType: string;
  ccaProgram: string;
  demandCreditProgram: string;
}

export interface UpdateKingBillingConfigBody {
  billingMode: string;
  billType: string;
  energyDiscount: string;
  demandDiscount: string;
  includeChart: boolean;
  includeSummary: boolean;
}
export interface SharedTenantAccountUpdateResponse {
  success: boolean;
  message: string;
}

export type UtilityType = 'PG&E' | 'SDG&E' | 'SCE';

export interface OptiRateTargetTariff {
    cuid: string;
    schedule: string
}

export interface CreateUtilityIncentivePlanBody {
  startingBalance: string;

  cadence: string;

  installmentCount: number;

  memo?: string;

  overrideBalanceCheck?: boolean;

  /** Per-installment amounts. When provided, the backend uses these instead of equal-splitting. */
  installmentAmounts?: string[];

  /**
   * Optional plan start date (ISO 8601). Controls when the first installment is due;
   * subsequent due dates cascade from this value. When omitted, the backend derives it
   * from the most recent king invoice due date + 1 month.
   */
  startDate?: string;
}

export interface IncentivePlanAuditEntry {
  /** Friendly human-readable action label. */
  action: string;
  /** Display name of the actor, or 'System' for automated events. */
  user: {
    cuid: string;
    email?: string;
    firstName?: string;
    lastName?: string;
  };
  timestamp: string;
  /** Free-text reason supplied by the actor at the time of the action. */
  reason: string | null;
  /** Underlying billing event audit log status (QUEUED | POSTED | SKIPPED). */
  status: string;
}

export interface CreateKingIncentivePlanBody
  extends CreateUtilityIncentivePlanBody {
  planType: string;
}

export interface BillingSdkTenantAccountBalances {
  kingOnlyBalance: string | null
  estimatedUtilityBalance: string | null
  isKingBalanceInflated?: boolean
}


export enum DocumentSourceEnum {
  TENANT_DOCUMENT = 'TENANT_DOCUMENT',
  INVOICE = 'INVOICE',
  PGE_DOB_SUMMARY = 'PGE_DOB_SUMMARY',
  PGE_BLUE_BILL_SUMMARY = 'PGE_BLUE_BILL_SUMMARY',
  SDGE_BILL_SUMMARY = 'SDGE_BILL_SUMMARY',
  SCE_BILL_SUMMARY = 'SCE_BILL_SUMMARY',
  S3_DIRECT = 'S3_DIRECT',
  TENANT_ACCOUNT_DOCUMENT = 'TENANT_ACCOUNT_DOCUMENT',
}

export interface DocumentMetadata {
  id: string
  type: string
  name: string
  modifiedDate: string
  s3Location: string
  downloadUrl: string
  source?: DocumentSourceEnum
}

export interface GetDocumentsArgs {
  page?: number;
  resultsPerPage?: number;
}

export type GetDocumentsData = Paginated<DocumentMetadata>;

export interface BillingEventEnums {
  eventTypes: string[];
  eventSubTypes: string[];
}

export interface ReverseInvoiceAccruedRequest {
  amount?: number;
  eventSubType?: string;
  modifiedDescription: string;
}

export interface EditBillingEventRequest {
  // BillingEvent fields
  eventType?: string;
  eventSubType?: string;
  postingDate?: string;
  transactionDate?: string;
  amount?: number;

  // BillingEntry fields - editable charge and fee amounts
  corpEnergyCharge?: number;
  corpDiscount?: number;
  utilityChargeTenantToCorp?: number;
  utilityChargeCorpToUtility?: number;
  unbilledPayableTenantToCorp?: number;
  payableTenantToCorp?: number;
  payableCorpToUtility?: number;
  corpNsfCharge?: number;
  corpLateFeeCharge?: number;
  corpProcessingCharge?: number;
  miscCharge?: number;
  dueTenantToCorp?: number;
  dueCorpToUtility?: number;

  modifiedDescription: string; // Required for audit trail
}

export interface VNEMAllocationItem {
  cuid: string;
  /** Benefitting meter id used on the percentage-edit body (WEB-7250). */
  meterCuid: string;
  percentage: number;
  meterNumber: string;
  utilityAccountNumber: string;
  utilityAccountName: string;
  serviceAddress: string;
  tariffSchedule: string;
  unallocatedCreditAccount: boolean;
  serviceNumber: string;
  apn: string;
  programName: string;
  smdIntervalDataRange?: {
    minDate: Date | null;
    maxDate: Date | null;
  };
  startDate: Date;
  endDate: Date;
  previousAllocation: VNEMAllocationItem | null;
}

export interface VNEMAllocationSubmission {
  cuid: string;
  submissionDate: Date;
  startDate: Date;
  endDate: Date;
  utilityEffectiveDate: Date | null;
  utilityEndDate: Date | null;
  isActiveAtUtility: boolean;
  isLatestSubmission: boolean;
  /** True when percentages were edited in OneBill rather than captured from Salesforce (WEB-7250). */
  hasLocalEdits: boolean;
  lastLocalEditAt: string | Date | null;
  vnemAllocations: VNEMAllocationItem[];
}

export interface UpdateVNEMPercentagesBody {
  effectiveDate: string;
  allocations: Array<{ meterCuid: string; percentage: number }>;
  modifiedDescription: string;
}

export interface UpdateVNEMPercentagesChange {
  meterCuid: string;
  meterNumber: string;
  previousPercentage: number;
  newPercentage: number;
}

export interface UpdateVNEMPercentagesResponse {
  changes: UpdateVNEMPercentagesChange[];
  sumAsOfEffectiveDate: number;
}

export interface GetVNEMAllocationsResponse {
  submissions: VNEMAllocationSubmission[];
  programBillingOn: boolean;
  hasActiveAtUtility: boolean;
}

export interface ActivateVNEMAllocationRequest {
  effectiveDate: string;
}

export interface ActivateVNEMAllocationResponse {
  success: boolean;
  message: string;
}

// Event Handling Report Types
export enum SystemSource {
  PGE_PORTAL = 'PGE_PORTAL',
  PGE_SHARE_MY_DATA = 'PGE_SHARE_MY_DATA',
  PGE_BILL = 'PGE_BILL',
  PGE_CUSTOMER_SERVICE = 'PGE_CUSTOMER_SERVICE',
  SCE_PORTAL = 'SCE_PORTAL',
  SCE_SHARE_MY_DATA = 'SCE_SHARE_MY_DATA',
  SCE_BILL = 'SCE_BILL',
  SCE_CUSTOMER_SERVICE = 'SCE_CUSTOMER_SERVICE',
  SDGE_PORTAL = 'SDGE_PORTAL',
  SDGE_SHARE_MY_DATA = 'SDGE_SHARE_MY_DATA',
  SDGE_BILL = 'SDGE_BILL',
  SDGE_CUSTOMER_SERVICE = 'SDGE_CUSTOMER_SERVICE',
  INTERNAL_SYSTEM = 'INTERNAL_SYSTEM',
  CUSTOMER_INPUT = 'CUSTOMER_INPUT',
  THIRD_PARTY_API = 'THIRD_PARTY_API',
}

export interface ProcessingRecord {
  processor: string;
  processedAt: string;
}

export interface ProcessingError {
  error: string;
  createdAt: string;
}

export interface RelatedEntity {
  entityType: string;
  entityId: string;
}

export interface EventReportEntry {
  id: string;
  transactionDate: string;
  postingDate: string;
  createdAt: string;
  createdBy: string;
  modifiedAt: string;
  modifiedBy: string;
  modifiedDescription?: string;
  systemSource: SystemSource | string;
  eventType: string;
  eventSubType?: string;
  payloadSchemaName: string;
  payloadSchemaVersion: number;
  payload: any;
  tags: string[];
  processingRecords: ProcessingRecord[];
  processingErrors: ProcessingError[];
  relatedEntities: RelatedEntity[];
  propertyName?: string;
  tenantName?: string;
}

export interface EventSummary {
  totalEvents: number;
  automatedEvents: number;
  successfulEvents: number;
  failedEvents: number;
  successRate: number;
  eventTypeBreakdown: Record<string, number>;
}

export interface GetEventReportArgs {
  systemSource?: SystemSource | string;
  eventType?: string;
  eventSubType?: string;
  startDate?: string;
  endDate?: string;
  processorFilter?: string;
  page?: number;
  resultsPerPage?: number;
}

// Ticket Types
export type TicketStatusEnum = 'OPEN' | 'IN_PROGRESS' | 'DONE' | 'CLOSED';
export type TicketSourceEnum = 'JIRA';

export interface TenantTicket {
  cuid: string;
  ticketKey: string;
  ticketUrl: string;
  summary: string;
  status: TicketStatusEnum;
  source: TicketSourceEnum;
  priority?: string;
  externalStatus?: string;
  createdAt: string;
  lastSyncedAt?: string;
  externalCreatedAt?: string;
  externalUpdatedAt?: string;
  matchedAccountNumber?: string;
  tenantAccountName?: string;
  tenantAccountNumber?: string;
}

export interface TicketSearchParams {
  status?: TicketStatusEnum;
  source?: TicketSourceEnum;
  page?: number;
  resultsPerPage?: number;
}

export interface TenantCallsSearchParams {
  page?: number;
  resultsPerPage?: number;
}

export interface TicketEnums {
  statuses: TicketStatusEnum[];
  sources: TicketSourceEnum[];
}

// Reconciliation Report Types

export type ReconciliationStatus = 'exact' | 'fuzzy' | 'mismatch' | 'missing' | 'sourceUnavailable';
export type ReconciliationSource = 'utilityPortal' | 'salesforce' | 'admin';
export type ReconciliationIssueType =
    | 'accountNameMismatch'
    | 'serviceIdPlaceholder'
    | 'serviceIdMismatch'
    | 'missingServiceAddress'
    | 'missingVnemAllocation'
    | 'scraperNotLinked'
    | 'inactiveMeter'
    | 'returnedPayment'
    | 'billingAddressMismatch';

export type ReconciliationViewPreset = 'all' | 'issuesOnly' | 'fullyReconciled' | 'mismatches';

export interface ReconciliationQueryParams {
    view?: ReconciliationViewPreset;
    statusIn?: string;
    hasAnyIssue?: boolean;
    issueType?: ReconciliationIssueType;
    include?: 'all' | 'summaryOnly' | 'rowsOnly';
}

export interface ReconciliationFieldValuesVO {
    utilityPortal?: string | number | boolean | null;
    salesforce?: string | number | boolean | null;
    admin?: string | number | boolean | null;
}

export interface ReconciliationMismatchVO {
    authoritativeValue: string | number | boolean | null;
    comparisonValue: string | number | boolean | null;
    remediationHintKey?: string;
}

export interface ReconciliationFieldVO {
    authoritativeSource: ReconciliationSource;
    status: ReconciliationStatus;
    values: ReconciliationFieldValuesVO;
    display?: string | number | boolean | null;
    mismatch?: ReconciliationMismatchVO;
}

export interface ReconciliationRowFieldsVO {
    accountName: ReconciliationFieldVO;
    serviceId: ReconciliationFieldVO;
    tariffRate: ReconciliationFieldVO;
    serviceAddress: ReconciliationFieldVO;
    billingAddress: ReconciliationFieldVO;
    vnemPct: ReconciliationFieldVO;
    scraperLinked: ReconciliationFieldVO;
    autopay: ReconciliationFieldVO;
    lastStmtDate: ReconciliationFieldVO;
    lastPaymentDate: ReconciliationFieldVO;
    lastPaymentAmount: ReconciliationFieldVO;
    paymentStatus: ReconciliationFieldVO;
    lastStmtDueDate: ReconciliationFieldVO;
    pmaApproved: ReconciliationFieldVO;
    onActiveTable: ReconciliationFieldVO;
    vaTableStatus: ReconciliationFieldVO;
}

export interface ReconciliationRowVO {
    utilityAccountNumber: string;
    tenantCuid: string;
    onebillAccountCuid: string;
    onebillAccountNumber: string;
    onebillAccountName: string | null;
    meterNumber: string | null;
    adminServiceAddress: string | null;
    adminBillingAddress: string | null;
    // True when a scraper_account row exists for this utility_account
    // (regardless of is_linked); false when no row exists. Lets the recon
    // table differentiate "no longer linked" (false) from "never seen by
    // scraper" (no row) without re-introducing a tri-state. WEB-6098.
    scraperAccountExists: boolean;
    // Latest billing mode for the tenant_account, sourced from
    // tenant_account_configuration.billing_mode where latest=true. Null when
    // no config row exists. WEB-6098.
    billingMode: 'CONSOLIDATED' | 'STANDALONE' | 'CONSOLIDATED_ESTIMATED' | 'STANDALONE_ESTIMATED' | 'BALANCE_ONLY' | null;
    rowStatus: ReconciliationStatus;
    issues: ReconciliationIssueType[];
    fields: ReconciliationRowFieldsVO;
    tenantType: string | null;
    isTopNationalTarget: boolean;
}

export interface ReconciliationReadinessScoreVO {
    percent: number;
    numerator: number;
    denominator: number;
}

export interface ReconciliationVnemAllocationSummaryVO {
    totalPercent: number;
    deviationFrom100: number;
    severity: 'ok' | 'warning' | 'critical' | 'unavailable';
}

export interface ReconciliationSummaryVO {
    totalRows: number;
    fullyReconciledRows: number;
    rowsWithIssues: number;
    readinessScore: ReconciliationReadinessScoreVO;
    statusCounts: Record<ReconciliationStatus, number>;
    vnemAllocation: ReconciliationVnemAllocationSummaryVO;
}

export interface ReconciliationDataCoverageVO {
    salesforceAvailable: boolean;
    adminAvailable: boolean;
    utilityPortalAvailable: boolean;
    scraperLastRun: string | null;
    totalAccountCount: number;
    scraperLinkedCount: number;
    vnemAllocatedCount: number;
    portalOnlyAccountCount: number;
    hiddenAccountCount: number;
}

export interface ReconciliationPropertyVO {
    programId: string;
    propertyCuid: string;
    propertyName: string;
    utilityShortName: string | null;
}

export interface ReconciliationUiHintIssueTypeVO {
    label: string;
    description: string;
    colorKey: 'green' | 'yellow' | 'orange' | 'red' | 'gray';
}

export interface ReconciliationUiHintTabPresetVO {
    label: string;
    filters: {
        statusIn?: ReconciliationStatus[];
        hasAnyIssue?: boolean;
    };
}

export interface ReconciliationUiHintsVO {
    issueTypes: Record<string, ReconciliationUiHintIssueTypeVO>;
    tabPresets: Record<string, ReconciliationUiHintTabPresetVO>;
}

export interface PortalOnlyAccountVO {
    utilityAccountNumber: string;
    accountName: string | null;
    active: boolean | null;
    serviceAddress: string | null;
    lastScraperRun: string | null;
    lastStmtDate: string | null;
    lastStmtAmount: number | null;
    lastPaymentDate: string | null;
    lastPaymentAmount: number | null;
    vnemPct: number | null;
}

export interface HiddenAccountServiceVO {
    saId: string;
    tariff: string;
    serviceAddress: string;
}

export interface HiddenAccountVO {
    accountNumber: string;
    accountName: string;
    lastBillDate: string | null;
    services: HiddenAccountServiceVO[];
}

export interface ReconciliationReportVO {
    schemaVersion: string;
    generatedAt: string;
    property: ReconciliationPropertyVO;
    dataCoverage: ReconciliationDataCoverageVO;
    summary: ReconciliationSummaryVO | null;
    issueCountsByType: Record<string, number>;
    rows: ReconciliationRowVO[];
    dataLimitations?: string[];
    portalOnlyAccounts?: PortalOnlyAccountVO[];
    hiddenAccounts?: HiddenAccountVO[];
    uiHints?: ReconciliationUiHintsVO;
}

// ---------------------------------------------------------------------------
// Go-Live Readiness (GET /admin/reconciliation/readiness)
// Fleet-wide Stage A gate rollup, grouped by property. WEB-6378.
// ---------------------------------------------------------------------------

// These types mirror the backend reconciliation.dto.ts (FleetReadinessVO and
// friends) field-for-field. The backend emits all UI-ready values — verdict,
// label, bucket, statusLabel, statusTone, bucketCounts — so the frontend never
// re-derives classification; it only picks colors and renders.

// Per-gate verdict (backend FleetGateVerdict). 'waiting-external' = blocked on
// the utility (the only non-KE owner); the owner field carries which KE team
// owns a 'fail-ours'.
export type GoLiveGateVerdict = 'pass' | 'fail-ours' | 'waiting-external' | 'unknown';

// Rolled-up status for a program/property (backend FleetRollupStatus).
export type GoLiveRollupStatus = 'blocking' | 'waiting-external' | 'passing' | 'unknown';

// Lifecycle bucket = the dashboard tabs (backend FleetBucket). Re-based on
// actual billing status (WEB-6425): 'billing' = actively invoicing. UI labels:
// 'recently-live' = "Recently Live", 'behind' = "Behind" (see BUCKETS).
export type GoLiveBucket = 'recently-live' | 'behind' | 'billing' | 'pre-pto';

// Status chip tone (backend FleetStatusTone). 'review' = billing but a gate
// fails (data-quality drift).
export type GoLiveStatusTone = 'blocking' | 'waiting' | 'ready' | 'review';

export interface GoLiveGateVO {
    // Stage A gate id, e.g. 'G_A_05_submission_covers_meters'.
    gate: string;
    // Backend-supplied short display label, e.g. 'Coverage'.
    label: string;
    verdict: GoLiveGateVerdict;
    // Raw SQL verdict (pass | fail | unknown) before normalization.
    rawVerdict: string;
    failureClass: string | null;
    owner: string | null;
    actionState: string | null;
}

export interface GoLiveProgramVO {
    programCuid: string;
    name: string;
    utility: string | null;
    // PTO date (ISO) if known.
    ptoDate: string | null;
    gates: GoLiveGateVO[];
    // VNEM coverage as a percentage, or null when not computable.
    vnemCoveragePct: number | null;
    // Percent of property output under TESA (WEB-6439), or null.
    tesaPercentOutput: number | null;
    // OneBill tenant-account coverage gauge (WEB-6439): a parallel billing-
    // readiness reading, NOT a submission blocker. Null when no expected accounts.
    oneBillAccounts: { expected: number; present: number } | null;
    owner: string | null;
    status: GoLiveRollupStatus;
    // Actually invoicing (non-void invoice covering service on/after PTO).
    isBilling: boolean;
    // Billing yet failing an actionable gate — data-quality drift, not a blocker.
    hasDataQualityIssue: boolean;
    bucket: GoLiveBucket;
    // Short status chip text, e.g. 'Billing', 'PTO drift', 'No submission'.
    statusLabel: string;
    statusTone: GoLiveStatusTone;
    // Count of non-hygiene, fail-ours gates that are NOT the binding gate
    // (WEB-6439): the parallel KE work hidden behind the single binding owner.
    // Rendered as a "+N to fix" indicator. Backend owns the classification.
    actionableFixCount: number;
    // Read-only next action for the binding (highest-priority open) gate
    // (WEB-6439), or null when the failure shape is unseeded. Rendered as a small
    // Action chip; backend owns the lookup.
    nextAction: { label: string; rung: number; ownerRole: string } | null;
}

export interface GoLivePropertyVO {
    propertyCuid: string;
    propertyName: string;
    utility: string | null;
    // Human-readable PTO rollup, e.g. '3/4 live'.
    ptoSummary: string;
    status: GoLiveRollupStatus;
    // True if any program at the property is billing-but-failing a gate.
    hasDataQualityIssue: boolean;
    bucket: GoLiveBucket;
    // Short label, e.g. '2 not billing · 1 review'.
    statusLabel: string;
    statusTone: GoLiveStatusTone;
    programs: GoLiveProgramVO[];
}

export interface GoLiveKpisVO {
    inScope: number;
    // Actively invoicing — the truth, regardless of gates.
    billing: number;
    // Past-PTO programs not yet billing (recently-live + behind worklist).
    worklist: number;
    // Billing programs still failing an actionable gate (data-quality drift).
    dataQuality: number;
}

export interface GoLiveBucketCountsVO {
    recentlyLive: number;
    behind: number;
    billing: number;
    prePto: number;
}

export interface GoLiveReadinessVO {
    schemaVersion: string;
    // ISO timestamps (Date serialized over the wire).
    generatedAt: string;
    asOf: string;
    kpis: GoLiveKpisVO;
    // Per-bucket program counts for the tab strip.
    bucketCounts: GoLiveBucketCountsVO;
    // PTO-age (days) at which a non-billing program moves recently-live -> behind.
    worklistSplitDays: number;
    properties: GoLivePropertyVO[];
    dataLimitations: string[];
}

export type TariffStatus = 'PENDING' | 'ACTIVE' | 'INACTIVE' | 'WAITING_FOR_APPROVAL' | 'APPROVED' | 'ARCHIVED'

export interface TariffItem {
  cuid: string
  schedule: string
  name: string | null
  status: TariffStatus
  utilityShortName: string | null
  /** Full utility name; used for CCA/DA list display as "Full Name (abbr)". */
  utilityName?: string | null
  utilityType: string | null
  effectiveDate: string | null
  modifiedAt: string
  modifiedBy: string
  /** Active meter-service usages for this tariff. */
  usageCount?: number
  /** Rate sheets linked to the current (non-archived, latest effective) rateset. */
  rateSheetCount?: number
  /** Source of the current (non-archived, latest effective) rateset. */
  source?: TariffHistorySource | null
}

export interface TariffRateItem {
  cuid: string
  name: string
  rate: number
  type: string
  category: string
  season: string | null
  touPeriod: string | null
  ratePeriodType: string | null
  rateGroup: string
  coincidental: boolean
  rateEffectiveDate: string
  rateEndDate: string | null
  vintageYear: string | null
  modifiedDescription: string | null
  tariffHistoryCuid?: string | null
}

export interface DetailedTariffItem {
  cuid: string
  schedule: string
  name: string | null
  status: TariffStatus
  salesforceId: string | null
  utilityCuid?: string | null
  utilityShortName: string | null
  utilityName?: string | null
  utilityType: string | null
  modifiedAt: string
  modifiedBy: string
  modifiedDescription: string | null
  rates: TariffRateItem[]
  /** Active meter services using this tariff. */
  activeUsageCount?: number
  /** All meter service rows referencing this tariff (active or inactive). */
  totalUsageCount?: number
  histories?: TariffHistoryItem[]
  selectedHistoryCuid?: string | null
}

export type TariffHistorySource = 'GENABILITY' | 'MANUAL' | 'UNKNOWN'

export type TariffRateSheetCategory = 'INDIVIDUAL' | 'GROUP'

export interface TariffHistoryAttachment {
  cuid: string
  label: string
  url: string
  contentType: string | null
  uploadedAt?: string
  isShared: boolean
  category: TariffRateSheetCategory
  vintageYear: string | null
  linkCount?: number
}

export interface TariffHistoryItem {
  cuid: string
  tariffCuid: string
  effectiveDate: string
  endDate: string | null
  status: TariffStatus
  source: TariffHistorySource
  notes?: string | null
  vintageYear?: string | null
  vintageYears?: string[]
  attachments: TariffHistoryAttachment[]
  rates?: TariffRateItem[]
}

export interface GetTariffsArgs {
  page?: number
  resultsPerPage?: number
  search?: string
  status?: TariffStatus
  utilityCuid?: string
  utilityType?: string
  sortBy?: string
  sortDir?: 'ASC' | 'DESC'
}

export type TariffStatusCounts = Partial<Record<TariffStatus, number>> & { ALL: number }

export type GetTariffsListData = Paginated<TariffItem> & {
  statusCounts: TariffStatusCounts
  pendingPastEffectiveCount?: number
}

export interface UpdateTariffBody {
  name?: string | null
  schedule?: string
  salesforceId?: string | null
  status?: TariffStatus
  modifiedDescription?: string | null
  reason?: string
}

export interface CreateTariffBody {
  utilityCuid: string
  schedule: string
  name?: string | null
  salesforceId?: string | null
  status?: TariffStatus
  effectiveDate?: string
  modifiedDescription?: string
}

export interface CreateTariffRateBody {
  name: string
  rate: number
  type: string
  category: string
  season?: string | null
  touPeriod?: string | null
  ratePeriodType?: string | null
  rateGroup?: string
  coincidental: boolean
  rateEffectiveDate: string
  rateEndDate?: string | null
  vintageYear?: string | null
  tariffHistoryCuid: string
  modifiedDescription?: string | null
  reason: string
}

export interface CreateTariffHistoryBody {
  effectiveDate: string
  notes?: string | null
  copyFromHistoryCuid?: string
  reason: string
}

export interface TariffHistoryReasonBody {
  reason: string
}

export interface TariffActivateOrphan {
  name: string
  rateGroup: string
  season: string | null
  touPeriod: string | null
  vintageYear: string | null
}

export interface TariffActivatePreview {
  historyCuid: string
  effectiveDate: string
  rateCount: number
  vintageYears: string[]
  usageCount: number
  effectiveDateInPast: boolean
  priorCurrent: {
    historyCuid: string
    effectiveDate: string
    endDate: string | null
    closesOn: string
    rateCount: number
  } | null
  orphanedRates: TariffActivateOrphan[]
}

export interface UpdateTariffRateBody {
  name?: string
  rate?: number
  season?: string | null
  touPeriod?: string | null
  rateGroup?: string
  coincidental?: boolean
  ratePeriodType?: string | null
  vintageYear?: string | null
  rateEffectiveDate?: string
  rateEndDate?: string | null
  modifiedDescription?: string
  reason: string
}

export interface TariffUsageItem {
  tenantCuid: string
  tenantName: string | null
  tenantAccountNumber: string | null
  meterServiceCuid: string
  utilityAccountNumber: string | null
  isActive: boolean
}

export type GetTariffUsagesData = Paginated<TariffUsageItem>

export type TariffAuditEntityType = 'TARIFF' | 'TARIFF_HISTORY' | 'TARIFF_RATE' | 'TARIFF_RATE_SHEET'

export interface TariffAuditUser {
  cuid: string
  email: string
  firstName: string
  lastName: string
}

export interface TariffAuditEntry {
  cuid: string
  action: string
  entityType: TariffAuditEntityType
  entityCuid: string
  entityLabel: string
  user: TariffAuditUser | string
  timestamp: string
  reason: string | null
  changes: Record<string, { before: unknown; after: unknown }> | null
}

export type GetTariffAuditHistoryData = Paginated<TariffAuditEntry>

export interface TariffRateAnalysisRow {
  schedule: string
  rateEffectiveDate: string
  season: string | null
  touPeriod: string | null
  vintageYear: string | null
  totalRate: number
  generationRate: number
  deliveryRate: number
  nbcRate: number
  deliveryPlusNbc: number
  deliveryPlusNbcWithoutWildfire: number
  generationDelta: number | null
  deliveryDelta: number | null
  nbcDelta: number | null
  totalDelta: number | null
  previousEffectiveDate: string | null
  generationDeltaPct: number | null
  deliveryDeltaPct: number | null
  nbcDeltaPct: number | null
  totalDeltaPct: number | null
  tariffName?: string | null
  tariffStatus?: TariffStatus | null
  ratesetStatus?: TariffStatus | null
}

export interface AccRateVintage {
  interconnectionApplicationYear: number
  startDate: string | null
  endDate: string | null
  rowCount: number
  rateNames: string[]
  loadedAt: string | null
}

export interface AccRateGridRow {
  month: number
  hour: number
  dayType: string
  generation: number | null
  delivery: number | null
  total: number | null
}

export interface CreateUtilityBody {
  name: string
  shortName: string
  type: 'IOU' | 'CCA' | 'ESP_DA'
  loginUrl?: string
}

export interface UpdateUtilityBody {
  name?: string
  shortName?: string
  type?: 'IOU' | 'CCA' | 'ESP_DA'
  loginUrl?: string | null
}

// --------------------------------------------------------------------------
// Program onboarding: program meters, property utilities, and utility taxes
// (WEB-7241 / WEB-7247 / WEB-7248). Shapes mirror the planned backend DTOs
// from WEB-7243 / WEB-7244 / WEB-7245 so the mock layer (and later the real
// API client from WEB-7246) can be swapped in without reshaping the UI.
// --------------------------------------------------------------------------

/** Minimal meter reference used for display and for selecting meters when adding a program meter. */
export interface ProgramMeterOption {
  cuid: string;
  meterNumber: string;
  serviceAddress?: string | null;
}

/** Program_meter membership row. `isInEffect` is server-owned ([start, end) UTC day, end exclusive). */
export interface ProgramMeterRow {
  cuid: string;
  benefittingMeter: ProgramMeterOption;
  startDate: string;
  endDate: string;
  isInEffect: boolean;
  modifiedBy?: string | null;
  modifiedDescription?: string | null;
  modifiedAt?: string | null;
}

export interface CreateProgramMeterBody {
  benefittingMeterCuid: string;
  startDate: string;
  endDate?: string | null;
  modifiedDescription: string;
}

export interface UpdateProgramMeterBody {
  startDate: string;
  endDate?: string | null;
  modifiedDescription: string;
}

export interface RetireProgramMeterBody {
  endDate?: string | null;
  modifiedDescription: string;
}

export interface UtilityOption {
  cuid: string;
  shortName: string;
  name: string;
  type?: string | null;
}

export interface EnergyTaxCatalogItem {
  cuid: string;
  taxName: string;
  code: string;
  taxRate: number;
  region?: string | null;
  /** Every charge type the tax applies to (the backend returns the full array). */
  applicableChargeType: string[];
  effectiveDate?: string | null;
  endDate?: string | null;
}

/** Mirrors the backend `UtilityChargeType` enum. */
export const UTILITY_CHARGE_TYPES = [
  'USAGE',
  'SERVICE',
  'GENERATION',
  'NON_GENERATION',
  'PCIA',
  'FFS',
  'CCA_GENERATION',
] as const;

export type UtilityChargeType = (typeof UTILITY_CHARGE_TYPES)[number];

export interface CreateEnergyTaxBody {
  taxName: string;
  /** Free text; billing matches some codes literally (e.g. `franchiseFeesSD`). */
  code: string;
  /** Fraction, not a percent: 0.0075 means 0.75%. */
  taxRate: number;
  region: string;
  applicableChargeType: UtilityChargeType[];
  effectiveDate: string;
  /** Omit for open-ended; the backend defaults it to 2030-12-31. */
  endDate?: string | null;
  modifiedDescription: string;
}

export interface PropertyUtilityTaxRow {
  cuid: string;
  energyTax: EnergyTaxCatalogItem;
  modifiedBy?: string | null;
  modifiedDescription?: string | null;
  modifiedAt?: string | null;
}

export interface PropertyUtilityRow {
  cuid: string;
  utility: UtilityOption;
  attachedTaxes: PropertyUtilityTaxRow[];
  modifiedBy?: string | null;
  modifiedDescription?: string | null;
  modifiedAt?: string | null;
}

export interface LinkPropertyUtilityBody {
  utilityCuid: string;
  modifiedDescription: string;
}

/** Result of a tax-sync (attach/detach) call, used to render a summary confirmation. */
export interface SyncPropertyUtilityTaxesResult {
  attached: EnergyTaxCatalogItem[];
  detached: EnergyTaxCatalogItem[];
  alreadyPresent: EnergyTaxCatalogItem[];
}

export interface SyncPropertyUtilityTaxesBody {
  /** Full desired set of attached energy tax cuids; the mock/API diffs against current state. */
  energyTaxCuids: string[];
  modifiedDescription: string;
}

// --------------------------------------------------------------------------
// Program onboarding: generating meters (WEB-7241 / WEB-7356). Net-new
// program_generating_meter pairings, distinct from the legacy program_meter.generating_meter_cuid column.
// Shapes mirror the planned backend DTO for the sibling generating-meters backend subtask so
// the mock layer (and later the real API client) can be swapped in without
// reshaping the UI.
// --------------------------------------------------------------------------

/** Meter reference for generating-meter selection, flagged with whether it has an active GENERATOR MeterService. */
export interface GeneratingMeterOption {
  cuid: string;
  meterNumber: string;
  serviceAddress?: string | null;
  /** False means the Conductor `ngom_meter_linked` gate will keep failing for this meter (warn, don't block). */
  hasActiveGeneratorMeterService: boolean;
  /** The property this NGOM sits on. Pairing UIs only offer meters on the program's own property. */
  propertyId: string;
}

export interface ProgramGeneratingMeterRow {
  cuid: string;
  generatingMeter: GeneratingMeterOption;
  startDate: string;
  /** Always a yyyy-mm-dd date; the server coerces omitted/null end dates to 2030-12-31. */
  endDate: string;
  /** Server-owned; UTC today in [startDate, endDate) (end exclusive). Do not recompute on the client. */
  isInEffect: boolean;
  /** Human-readable actor (email or name), resolved server-side from stored user CUID. */
  modifiedBy?: string | null;
  modifiedDescription?: string | null;
  modifiedAt?: string | null;
}

export interface CreateProgramGeneratingMeterBody {
  generatingMeterCuid: string;
  startDate: string;
  endDate?: string | null;
  modifiedDescription: string;
}

export interface UpdateProgramGeneratingMeterBody {
  startDate: string;
  endDate?: string | null;
  modifiedDescription: string;
}

export interface RetireProgramGeneratingMeterBody {
  /** Final pairing end date. Omit to let the server use max(UTC today, start date). */
  endDate?: string | null;
  modifiedDescription: string;
}
